import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaCog, FaBell, FaUserCircle, FaBars, FaChevronDown } from 'react-icons/fa';
//import logoUrl from '../../pawafleet-logo-dark.svg';
import logoUrl from '../assets/logo.jpg';
import { useAuth } from './AuthContext'; // Adjust the path as needed

const { rootUrl } = require('../../config/config');

const Header = ({ toggleSidebar, isSidebarOpen }) => {
  // Fetch user data from AuthContext
  const { user, logout, token, isAuthenticated } = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !token) {
      setUnreadCount(0);
      return undefined;
    }

    let cancelled = false;
    let stream = null;

    const loadUnreadCount = async () => {
      try {
        const response = await fetch(`${rootUrl}/api/notifications/unread-count`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        const data = await response.json();
        if (!cancelled && data.success) {
          setUnreadCount(data.data?.unread_count || 0);
        }
      } catch (error) {
        if (!cancelled) {
          setUnreadCount(0);
        }
      }
    };

    loadUnreadCount();

    try {
      stream = new EventSource(`${rootUrl}/api/notifications/stream?token=${encodeURIComponent(token)}`);
      stream.addEventListener('notification', () => {
        loadUnreadCount();
      });
      stream.addEventListener('ready', () => {
        loadUnreadCount();
      });
    } catch (error) {
      stream = null;
    }

    return () => {
      cancelled = true;
      if (stream) {
        stream.close();
      }
    };
  }, [token, isAuthenticated]);

  const handleLogout = () => {
    logout();
    setIsDropdownOpen(false);
    navigate('/login');
  };

  const handleResetPassword = () => {
    setIsDropdownOpen(false);
    navigate('/reset-password');
  };

  const handleProfile = () => {
    setIsDropdownOpen(false);
    navigate('/profile');
  };

  return (
    <header className="bg-gradient-to-r from-brand-secondary via-brand-secondary/95 to-brand-secondary shadow-lg text-white fixed top-0 left-0 right-0 z-50 h-14 border-b border-brand-primary/20">
      <div className="flex h-full justify-between items-center px-3 sm:px-4">
        {/* Branding Section */}
        <div className="flex items-center space-x-2.5 sm:space-x-3">
          {/* Mobile Menu Toggle Button */}
          <button
            type='button'
            onClick={toggleSidebar}
            className="md:hidden p-1 rounded-md hover:bg-brand-primary/20 transition-colors duration-200"
            aria-label="Toggle Sidebar"
          >
            <FaBars className="text-base" />
          </button>
          
          <button
            onClick={toggleSidebar}
            className="flex items-center hover:opacity-95 transition duration-200 focus:outline-none focus:ring-2 focus:ring-brand-primary/50 rounded-lg p-0"
            aria-label="Toggle Sidebar"
          >
            <img
              src={logoUrl}
              alt="PAWAFLEET"
              className="h-8 rounded-lg sm:h-9 md:h-10 w-auto object-contain drop-shadow-md transition-transform duration-200 hover:scale-[1.02]"
            />
          </button>
        </div>

        {/* Actions Section */}
        <div className="flex items-center space-x-2 sm:space-x-3">
          {/* Notifications */}
          <Link
            to="/notifications"
            className="relative p-1 rounded-full hover:bg-brand-primary/20 transition-colors duration-200 group"
            aria-label="Notifications"
          >
            <FaBell className="text-sm sm:text-base group-hover:text-brand-primary transition-colors duration-200" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-brand-primary text-[10px] text-white rounded-full px-1.5 py-0 font-medium leading-4 min-w-[18px] text-center shadow-md">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </Link>

          {/* Settings */}
          <Link
            to="/cosmosettings"
            className="p-1 rounded-full hover:bg-brand-primary/20 transition-colors duration-200 group"
            aria-label="Settings"
          >
            <FaCog className="text-sm sm:text-base group-hover:text-brand-primary transition-colors duration-200" />
          </Link>

          {/* User Profile with Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="flex items-center space-x-1.5 p-1 rounded-full hover:bg-brand-primary/20 transition-colors duration-200 group cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-primary/50"
              aria-label="User Menu"
              aria-expanded={isDropdownOpen}
            >
              {user?.profile_picture ? (
                <img
                  src={`${rootUrl}${user.profile_picture}`}
                  alt={user.name || 'User'}
                  className="w-6 h-6 sm:w-7 sm:h-7 rounded-full object-cover border-2 border-brand-primary/30 group-hover:border-brand-primary transition-colors duration-200"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              ) : (
                <FaUserCircle className="text-lg sm:text-xl group-hover:text-brand-primary transition-colors duration-200" />
              )}
              <span className="text-xs sm:text-sm font-medium text-brand-light group-hover:text-brand-primary transition-colors duration-200">
                {user ? user.name?.split(' ')[0] : 'User'}
              </span>
              <FaChevronDown
                className={`inline-block text-[10px] sm:text-xs transition-transform duration-200 ${
                  isDropdownOpen ? 'rotate-180' : ''
                }`}
              />
            </button>

            {/* Dropdown Menu */}
            {isDropdownOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50">
                <div className="px-4 py-2 border-b border-gray-100">
                  <p className="text-sm font-medium text-gray-800">{user?.name || 'User'}</p>
                  <p className="text-xs text-gray-500">{user?.email || ''}</p>
                </div>
                
                <button
                  onClick={handleResetPassword}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-150 flex items-center space-x-3"
                >
                  <span>🔐</span>
                  <span>Reset Password</span>
                </button>

                <button
                  onClick={handleProfile}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-150 flex items-center space-x-3"
                >
                  <span>👤</span>
                  <span>Profile</span>
                </button>

                <button
                  onClick={handleLogout}
                  className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors duration-150 flex items-center space-x-3 border-t border-gray-100"
                >
                  <span>🚪</span>
                  <span>Logout</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
